/**
 * The create / edit / delete dialog state a CRUD screen holds.
 *
 * Five screens were each hand-rolling the same three `useState`s plus a form
 * object, and the bug they all had room for is the same one: opening the edit
 * dialog and forgetting to prefill it, or closing it and forgetting to clear
 * the row — so the next create starts with the last edit's values in it.
 * `openEdit` does both halves, and there is no way to do one without the other.
 *
 * It holds **state, not calls.** The submit still belongs to the page, through
 * `useSubmit`, because only the page knows which endpoint it is and what the
 * body looks like. That keeps the rule the rest of `hooks/` follows: nothing in
 * here imports from `../api/`.
 *
 * ```tsx
 * const crud = useCrudDialogs<RuleDto, RuleForm>({ blank: BLANK_FORM, formFor });
 *
 * const save = useSubmit(async () => {
 *   if (!crud.editing) return undefined;
 *   const result = await updateRule(crud.editing.id, changesBetween(crud.editing, crud.form));
 *   rules.reload();
 *   return result;
 * });
 *
 * <Button onClick={() => crud.openCreate()}>New</Button>
 * <FormDialog open={crud.isOpen} onClose={crud.close} … />
 * ```
 */
import { useCallback, useRef, useState } from 'react';

export interface UseCrudDialogsOptions<Row, Form> {
  /** The form a create starts from. */
  blank: Form;
  /** How an existing row becomes form values, for an edit. */
  formFor: (row: Row) => Form;
}

export interface CrudDialogsState<Row, Form> {
  /** The values in the dialog, whichever mode it is in. */
  form: Form;
  setForm: (form: Form) => void;
  /** Changes some of the fields. `patch({ name })` rather than a full spread. */
  patch: (changes: Partial<Form>) => void;

  /** `create` or `edit` while the form dialog is open, otherwise `null`. */
  mode: 'create' | 'edit' | null;
  /** Convenience for `mode !== null` — what `FormDialog`'s `open` wants. */
  isOpen: boolean;
  /** The row being edited, or `null` in create mode. Narrow on this. */
  editing: Row | null;
  /** The row a confirmation is asking about. Its own dialog, its own state. */
  deleting: Row | null;

  /** Opens a create, resetting to `blank`. `overrides` seeds a field. */
  openCreate: (overrides?: Partial<Form>) => void;
  /** Opens an edit, prefilled from the row. Both halves, always. */
  openEdit: (row: Row) => void;
  openDelete: (row: Row) => void;
  /** Closes the form dialog. Leaves a delete confirmation alone. */
  close: () => void;
  closeDelete: () => void;
}

export function useCrudDialogs<Row, Form>(
  options: UseCrudDialogsOptions<Row, Form>,
): CrudDialogsState<Row, Form> {
  const { blank, formFor } = options;

  const [form, setForm] = useState<Form>(blank);
  const [mode, setMode] = useState<'create' | 'edit' | null>(null);
  const [editing, setEditing] = useState<Row | null>(null);
  const [deleting, setDeleting] = useState<Row | null>(null);

  const patch = useCallback((changes: Partial<Form>) => {
    setForm((current) => ({ ...current, ...changes }));
  }, []);

  // Read through a ref, the way `useSubmit` reads its operation. A page that
  // builds `blank` or `formFor` inline hands over a new one every render; a
  // dependency on either would rebuild these callbacks every render, and no
  // dependency at all would freeze the first render's copy. The ref is neither.
  const latest = useRef({ blank, formFor });
  latest.current = { blank, formFor };

  const openCreate = useCallback((overrides?: Partial<Form>) => {
    const { blank: fresh } = latest.current;
    setForm(overrides ? { ...fresh, ...overrides } : fresh);
    setEditing(null);
    setMode('create');
  }, []);

  const openEdit = useCallback((row: Row) => {
    setForm(latest.current.formFor(row));
    setEditing(row);
    setMode('edit');
  }, []);

  const openDelete = useCallback((row: Row) => setDeleting(row), []);

  const close = useCallback(() => {
    setMode(null);
    setEditing(null);
  }, []);

  const closeDelete = useCallback(() => setDeleting(null), []);

  return {
    form,
    setForm,
    patch,
    mode,
    isOpen: mode !== null,
    editing,
    deleting,
    openCreate,
    openEdit,
    openDelete,
    close,
    closeDelete,
  };
}
